import clsx from 'clsx';
import { type FC, useMemo } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import type { DealerBoothListElementDto } from '@furality/fox-client/models';

// import TooltipHelper from '@/components/TooltipHelper.tsx';

import {
  BoothTab,
  BoothTabIdIncrease,
  BoothTabSection,
  BoothTabSectionIdIncrease,
  BoothTabTheme,
} from './BoothEnums.ts';
import style from './Map.module.scss';
import MapBooth from './MapBooth.tsx';
import MapButtons from './MapButtons.tsx';
import { tabI18nKey } from './tabI18n.ts';

type MapProps = {
  onBoothSelect: (booth: number) => void;
  onSectionSelect: (section: BoothTabSection) => void;
  highlightedBoothIndex: number;
  dealers: DealerBoothListElementDto[];
  /**
   * True if the map is in mobile view.
   */
  isMobileMapEnabled?: boolean;
  controls?: boolean;
  /**
   * onCloseButtonClick - function to call when the close button is clicked
   * The close button only shows up in mobile, when the map acts more like a modal.
   */
  onCloseButtonClick?: () => void;

  currentMap: BoothTab;
  currentMapSection: BoothTabSection;
};

const Map: FC<MapProps> = ({
  onBoothSelect,
  // onSectionSelect,
  highlightedBoothIndex,
  isMobileMapEnabled,
  controls,
  onCloseButtonClick,
  currentMap,
  currentMapSection,
  dealers,
}) => {
  const { t } = useTranslation(['common', 'dealers']);

  const mapNumber = useMemo(() => {
    const worlds: BoothTab[] = [BoothTab.WORLD1];
    return worlds.indexOf(currentMap) + 1;
  }, [currentMap]);

  const boothsIdLevel = useMemo(() => {
    return BoothTabIdIncrease[currentMap] + BoothTabSectionIdIncrease[currentMapSection];
  }, [currentMap, currentMapSection]);

  const themeStyles = useMemo(
    () => ({
      '--map-theme': BoothTabTheme[currentMap],
    }),
    [currentMap],
  );

  const getBoothName = (boothIndex: number) => {
    const displayBoothIndex = boothsIdLevel + boothIndex;
    const boothAtPosition = dealers.find((booth) => booth.boothId === displayBoothIndex);

    return boothAtPosition
      ? `Booth ${displayBoothIndex}: ${boothAtPosition.name}`
      : `Booth ${displayBoothIndex}`;
  };

  const isSafari =
    typeof navigator !== 'undefined' && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  return (
    <div className={clsx(style['reactTransformFix'], 'rounded overflow-hidden')}>
      <TransformWrapper
        disabled={!controls}
        limitToBounds
        initialScale={1}
        minScale={1}
        initialPositionX={0}
        initialPositionY={0}
        maxPositionX={0}
        maxPositionY={0}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            {controls && (
              <MapButtons
                isMobileMapEnabled={isMobileMapEnabled}
                zoomIn={zoomIn}
                zoomOut={zoomOut}
                resetTransform={resetTransform}
                closeMap={onCloseButtonClick}
                buttonPosition="dealers"
              />
            )}

            {/* CON-SPECIFIC: con-map */}
            <TransformComponent>
              {/*<svg viewBox="0 0 4096 4096" style={{ overflow: 'auto' }}>*/}
              <Container className={clsx(style['maps'], 'mw-100')} style={themeStyles}>
                <Row xs="1" lg="2" className="w-100 justify-content-center">
                  <Col style={{ height: 'fit-content' }}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 1278 1278"
                      className="w-100 h-100"
                      aria-label={t('shared.dealers.map.aria_label')}
                    >
                      <text x={20} y={200} fontSize={75}>
                        {t(`dealers:tab.${currentMap}`)}
                      </text>
                      <text x={20} y={270} fontSize={48}>
                        {t('dealers:island.number', {
                          current: mapNumber,
                          max: 3,
                        })}
                      </text>
                      {currentMap === BoothTab.WORLD1 && (
                        <image href="https://media.cdn.furality.online/image/f8/portal/explore/dealers-den/den1.webp" />
                      )}
                      {/* {currentMap === BoothTab.WORLD2 && (
                        <image href="https://media.cdn.furality.online/image/f8/portal/explore/dealers-den/den2.webp" />
                      )}
                      {currentMap === BoothTab.WORLD3 && (
                        <image href="https://media.cdn.furality.online/image/f8/portal/explore/dealers-den/den3.webp" />
                      )} */}
                      {/* <g>
                        <TooltipHelper
                          id={`section-btn-${BoothTabSection.UPPER}-tooltip`}
                          title={t(`dealers:tab.section.${BoothTabSection.UPPER}`)}
                          child={
                            <g
                              className="section-btn"
                              tabIndex={0}
                              role="button"
                              aria-pressed={currentMapSection === BoothTabSection.UPPER}
                              aria-labelledby={`section-btn-${BoothTabSection.UPPER}-label`}
                              onClick={() => onSectionSelect(BoothTabSection.UPPER)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === 'Space') {
                                  onSectionSelect(BoothTabSection.UPPER);
                                }
                              }}
                            >
                              <path
                                id="Upper"
                                d="M928.1,423.97c0-9.29-6.71-15.87-17.16-19.74s-25.03-11.87-63.61-13.55c-32.29-1.4-73.98,1.98-86.71,3.12.47-.74,1.01-2.17.52-4.54-.77-3.74-4.27-3.52-4.27-3.52,0,0-7.98-23.06-37.66-23.06s-39.61,26.32-39.61,26.32c0,0-4.26,0-4.52,4.64-.16,2.83,1.94,4.13,1.94,4.13,0,0,1.03,11.23,4.26,20.65,3.23,9.42,6.06,11.74,6.06,11.74,0,0-1.2,4.05,1.03,6.71.06.07.12.12.18.18-.18,2-.27,4.26-.05,6.01.52,4,1.94,4.52,3.87,6.84s-1.29,3.1-.77,4.39c.52,1.29.9,1.81,3.1.9,1.92-.79,9.21-2.18,10.97-2.51,0,2.03-.16,8.21-.22,10.97-1.36.16-2.32.54-2.61,1.35-.13.36-.17,1.12-.14,2.13-1.8.06-3.66.13-5.53.2-14.19.52-24.13,3.61-33.94,7.1-9.81,3.48-10.06,12.13-9.94,20.9s11.23,7.23,19.74,10.32c8.52,3.1,24.77,5.68,28.13,6.06s3.74,2.32,6.19,1.55c2.45-.77,2.32-5.29,4.13-7.35s2.19-2.58,5.55,0,5.16,6.32,7.35,6.58c2.19.26,2.19-1.68,5.68-2.32,3.48-.65,18.71-.26,20.9.13,2.19.39,3.74,2.97,4.9,2.97s5.81-5.03,8.26-5.68c2.45-.65,9.68-2.71,12.52-3.74,2.84-1.03,1.29-7.87,1.03-13.16-.26-5.29-3.1-8.9-3.1-8.9,0,0,8,2.97,11.87,5.42s6.84.52,10.58-1.16,13.55,1.42,17.55,0c4-1.42,1.16-2.45,5.68-6.97,4.52-4.52,9.42-4.9,22.36-1.42s20.22,5.68,24.22,3.1c4-2.58,1.94-5.68,3.23-10.45,1.29-4.77,5.81-2.97,12.52-2.06,6.71.9,11.48-1.42,16.52-4.65,5.03-3.23,2.19-7.87,2.19-7.87,0,0,12.39-1.29,16.77-1.29s7.1-3.48,9.16-6.06c2.06-2.58.9-15.1.9-24.39ZM708.74,453.9l15.48-.13.61,9.68c-2.46.14-5.38.51-8.14.51s-5.79-.3-8.29-.39l.34-9.68Z"
                              />
                            </g>
                          }
                        />
                        <TooltipHelper
                          id={`section-btn-${BoothTabSection.MIDDLE}-tooltip`}
                          title={t(`dealers:tab.section.${BoothTabSection.MIDDLE}`)}
                          child={
                            <g
                              className="section-btn"
                              tabIndex={0}
                              role="button"
                              aria-pressed={currentMapSection === BoothTabSection.MIDDLE}
                              onClick={() => onSectionSelect(BoothTabSection.MIDDLE)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === 'Space') {
                                  onSectionSelect(BoothTabSection.MIDDLE);
                                }
                              }}
                            >
                              <path
                                id="Middle"
                                d="M474.03,583.19c-47.43-3.95-80.05.43-97.03,3.95-5.83-4.69-14.43-8.73-26.85-8.73-29.68,0-39.61,26.32-39.61,26.32,0,0-4.26,0-4.52,4.64-.16,2.83,1.94,4.13,1.94,4.13,0,0,1.03,11.23,4.26,20.65,3.23,9.42,6.06,11.74,6.06,11.74,0,0-1.2,4.05,1.03,6.71,2.71,3.23,7.48,1.68,10.58,5.03,3.1,3.35,6.71,8.26,6.71,10.58,0,1.8-.15,8.32-.22,11.17-1.36.16-2.32.54-2.61,1.35-.26.7-.17,2.88.12,5.51-6.61,2.26-10.57,5.14-10.79,8.31l-.94,6.82c-.65,12.02,22.57,21.71,56.53,24.08,33.96,2.36,65.72-4.56,66.31-13.11l.9-10.73c.59-8.55-26.45-17.39-60.41-19.76-1.23-.09-2.45-.16-3.66-.22-1.5-2.11-3.08-4.73-2.5-5.68,1.03-1.68,15.48-12.9,17.29-13.29,1.81-.39,6.84,4.13,8.26,4.52s5.42-2.32,7.35-1.42c1.94.9,2.19,3.1,4,3.1s3.74-2.32,6.06-1.68c2.32.65,6.06,3.1,9.03,3.1s17.29-11.1,19.35-11.1,13.68,9.29,15.35,9.55c1.68.26,5.42-11.23,6.97-11.23s2.06,8.9,6.71,10.32c4.65,1.42,11.74-1.03,15.87-1.03s8.77-5.81,12.52-8.26c3.74-2.45,7.61-4.39,13.94-6.06,6.32-1.68,18.97-9.1,19.87-12.48.9-3.39,2.58-23.9,2.71-26.35s2.19-24.39-70.58-30.45ZM339.71,669.65l15.48-.13.61,9.68c-2.46.14-5.38.51-8.14.51s-5.79-.3-8.29-.39l.34-9.68Z"
                              />
                            </g>
                          }
                        />
                        <TooltipHelper
                          id={`section-btn-${BoothTabSection.LOWER}-tooltip`}
                          title={t(`dealers:tab.section.${BoothTabSection.LOWER}`)}
                          child={
                            <g
                              className="section-btn"
                              tabIndex={0}
                              role="button"
                              aria-pressed={currentMapSection === BoothTabSection.LOWER}
                              onClick={() => onSectionSelect(BoothTabSection.LOWER)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === 'Space') {
                                  onSectionSelect(BoothTabSection.LOWER);
                                }
                              }}
                            >
                              <path
                                id="Lower"
                                d="M751.97,731.32c-2.25-1.97-4.55-3.71-6.7-5.22,2.63-8.3,1.67-17.36,1.67-17.36,0,0,1.81-1.42,1.03-5.16-.77-3.74-4.27-3.52-4.27-3.52,0,0-7.98-23.06-37.66-23.06s-39.61,26.32-39.61,26.32c0,0-3.83,0-4.44,3.99-4.9-.5-9.44-.82-13.37-.89-27.74-.52-62.06.9-86.45,7.23-24.39,6.32-31.48,10.45-37.68,15.23-6.19,4.77-9.8,10.45-9.81,13.29-.02,5.03-.26,25.42,1.03,27.1,1.29,1.68,2.06,4.9,4.65,5.94,2.58,1.03,2.97-.9,3.87,1.03.9,1.94-1.29,2.32,3.74,3.61,5.03,1.29,12.77,7.74,14.58,8.9,1.81,1.16,6.84.26,13.29,2.71,6.45,2.45,17.42,5.55,21.16,5.68s3.48-2.06,2.58-3.61c-.9-1.55,8.77-.39,13.81-.39s48.9-1.16,48.9-1.16c0,0-2.84,2.84-4.52,7.35-1.68,4.52-1.29,14.19,9.29,17.42,10.58,3.23,42.06,7.48,59.61,6.45,17.55-1.03,30.06-4.9,35.35-9.94,5.29-5.03,7.87-10.84-2.58-15.1s-21.81-5.29-21.81-5.29l-1.16-.1c.53-2.65.92-5.77,1.09-8.37,1.79,1.03,5.05,1.86,10.53.08,9.55-3.1,9.68-9.03,11.74-9.68,2.06-.65,6.32,2.58,8.52,1.03s2.71-3.61,2.84-5.68,9.29-6.71,8.77-15.61c-.52-8.9.26-16-8-23.23Z"
                              />
                            </g>
                          }
                        />
                      </g> */}
                    </svg>
                  </Col>
                  <Col
                    className={clsx({
                      'd-none': currentMapSection === BoothTabSection.ALL,
                    })}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 1278 1278"
                      className={clsx('w-100 h-100')}
                      role="radiogroup"
                    >
                      <g role="radiogroup">
                        <g id="World_1_Bases_-_Pane">
                          <path
                            className="booths-bg"
                            id="fill_at_70_x25__fill"
                            d="M646.07,25.29C306.62,22,27.67,296.78,26.08,636.25c-1.16,247.45,144.1,461.1,354.13,559.33,5.38,2.51,11.77.11,14.19-5.31l46.97-105.61c2.35-5.29,8.49-7.69,13.83-5.45,56.84,23.9,119.28,37.12,184.8,37.12s127.79-13.18,184.57-37.02c5.33-2.24,11.46.16,13.82,5.44l47.13,105.58c2.42,5.42,8.81,7.81,14.19,5.29,209.25-97.83,354.23-310.2,354.23-556.44,0-337.03-271.59-610.64-607.85-613.89Z"
                          />
                          <path
                            className="booths-bg2"
                            id="keyline"
                            d="M646.07,25.29C306.62,22,27.67,296.78,26.08,636.25c-1.16,247.45,144.1,461.1,354.13,559.33,5.38,2.51,11.77.11,14.19-5.31l46.97-105.61c2.35-5.29,8.49-7.69,13.83-5.45,56.84,23.9,119.28,37.12,184.8,37.12s127.79-13.18,184.57-37.02c5.33-2.24,11.46.16,13.82,5.44l47.13,105.58c2.42,5.42,8.81,7.81,14.19,5.29,209.25-97.83,354.23-310.2,354.23-556.44,0-337.03-271.59-610.64-607.85-613.89Z"
                          />
                        </g>
                        {!isSafari && (
                          <foreignObject
                            className="booths-text"
                            x="0"
                            y="0"
                            width="100%"
                            height="100%"
                          >
                            <div>
                              <p style={{ fontSize: '2.5rem' }}>
                                {t('dealers:island.name', {
                                  number: mapNumber,
                                  title: t(tabI18nKey(currentMap)),
                                })}
                              </p>
                              <p
                                style={{
                                  fontSize: '4.75rem',
                                  color: '#ffd5a2',
                                }}
                              >
                                {currentMapSection !== BoothTabSection.ALL &&
                                  t(`dealers:tab.section.${currentMapSection}`)}
                              </p>
                              <p
                                style={{
                                  fontSize: '3rem',
                                  lineHeight: 1,
                                  marginTop: '-0.75rem',
                                }}
                              >
                                {t('dealers:booths_range', {
                                  min: boothsIdLevel,
                                  max: boothsIdLevel + 53,
                                })}
                              </p>
                            </div>
                          </foreignObject>
                        )}
                        <g id="World_1_Bases_-_Booths">
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={51}
                            boothName={getBoothName(51)}
                            id="Special__x28_FWA_x2F_EOZ_x2F_Merch_x29_"
                            x="752.58"
                            y="201.15"
                            width="78.58"
                            height="78.58"
                            transform="translate(1297.48 -414.71) rotate(110.77)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={50}
                            boothName={getBoothName(50)}
                            id="Charity"
                            x="449.98"
                            y="200.72"
                            width="78.58"
                            height="78.58"
                            transform="translate(540.19 -302.57) rotate(69.23)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={53}
                            boothName={getBoothName(53)}
                            id="Staff_2"
                            x="786.11"
                            y="112.74"
                            width="78.58"
                            height="78.58"
                            transform="translate(1260.24 -565.81) rotate(110.77)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={52}
                            boothName={getBoothName(52)}
                            id="Staff_1"
                            x="416.46"
                            y="112.32"
                            width="78.58"
                            height="78.58"
                            transform="translate(435.9 -328.28) rotate(69.23)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={31}
                            boothName={getBoothName(31)}
                            id="_x30_31"
                            x="990.44"
                            y="945.98"
                            width="78.58"
                            height="78.58"
                            transform="translate(1147.14 2405.6) rotate(-138.46)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={30}
                            boothName={getBoothName(30)}
                            id="_x30_30"
                            x="1061.94"
                            y="842.66"
                            width="78.58"
                            height="78.58"
                            transform="translate(1666.46 2174.65) rotate(-152.31)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={29}
                            boothName={getBoothName(29)}
                            id="_x30_29"
                            x="1106.64"
                            y="725.22"
                            width="78.58"
                            height="78.58"
                            transform="translate(2075.6 1781.04) rotate(-166.15)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={28}
                            boothName={getBoothName(28)}
                            id="_x30_28"
                            x="1121.93"
                            y="600.5"
                            width="78.58"
                            height="78.58"
                            transform="translate(2322.43 1279.59) rotate(-180)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={27}
                            boothName={getBoothName(27)}
                            id="_x30_27"
                            x="1106.94"
                            y="475.75"
                            width="78.58"
                            height="78.58"
                            transform="translate(2382.41 740.8) rotate(166.15)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={26}
                            boothName={getBoothName(26)}
                            id="_x30_26"
                            x="1062.51"
                            y="358.21"
                            width="78.58"
                            height="78.58"
                            transform="translate(2262.13 237.44) rotate(152.31)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={25}
                            boothName={getBoothName(25)}
                            id="_x30_25"
                            x="991.26"
                            y="254.72"
                            width="78.58"
                            height="78.58"
                            transform="translate(1996.89 -169.3) rotate(138.46)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={24}
                            boothName={getBoothName(24)}
                            id="_x30_24"
                            x="897.3"
                            y="171.28"
                            width="78.58"
                            height="78.58"
                            transform="translate(1641.94 -440.61) rotate(124.62)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={23}
                            boothName={getBoothName(23)}
                            id="_x30_23"
                            x="305.13"
                            y="170.58"
                            width="78.58"
                            height="78.58"
                            transform="translate(321.49 -192.8) rotate(55.38)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={22}
                            boothName={getBoothName(22)}
                            id="_x30_22"
                            x="210.98"
                            y="253.78"
                            width="78.58"
                            height="78.58"
                            transform="translate(257.28 -92.25) rotate(41.54)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={21}
                            boothName={getBoothName(21)}
                            id="_x30_21"
                            x="139.48"
                            y="357.12"
                            width="78.58"
                            height="78.58"
                            transform="translate(204.7 -37.67) rotate(27.69)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={20}
                            boothName={getBoothName(20)}
                            id="_x30_20"
                            x="94.78"
                            y="474.55"
                            width="78.58"
                            height="78.58"
                            transform="translate(126.87 -17.15) rotate(13.85)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={19}
                            boothName={getBoothName(19)}
                            id="_x30_19"
                            x="79.49"
                            y="599.28"
                            width="78.58"
                            height="78.58"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={18}
                            boothName={getBoothName(18)}
                            id="_x30_18"
                            x="94.49"
                            y="724.02"
                            width="78.58"
                            height="78.58"
                            transform="translate(-178.78 54.2) rotate(-13.85)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={17}
                            boothName={getBoothName(17)}
                            id="_x30_17"
                            x="138.9"
                            y="841.57"
                            width="78.58"
                            height="78.58"
                            transform="translate(-388.95 183.71) rotate(-27.69)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={16}
                            boothName={getBoothName(16)}
                            id="_x30_16"
                            x="210.16"
                            y="945.06"
                            width="78.58"
                            height="78.58"
                            transform="translate(-590.01 412.97) rotate(-41.54)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={15}
                            boothName={getBoothName(15)}
                            id="_x30_15"
                            x="919.67"
                            y="883.29"
                            width="78.58"
                            height="78.58"
                            transform="translate(1064.98 2249.05) rotate(-138.46)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={14}
                            boothName={getBoothName(14)}
                            id="_x30_14"
                            x="978.23"
                            y="798.72"
                            width="78.58"
                            height="78.58"
                            transform="translate(1529.04 2052.9) rotate(-152.31)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={13}
                            boothName={getBoothName(13)}
                            id="_x30_13"
                            x="1014.84"
                            y="702.59"
                            width="78.58"
                            height="78.58"
                            transform="translate(1900.09 1714.48) rotate(-166.15)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={12}
                            boothName={getBoothName(12)}
                            id="_x30_12"
                            x="1027.38"
                            y="600.5"
                            width="78.58"
                            height="78.58"
                            transform="translate(2133.35 1279.59) rotate(-180)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={11}
                            boothName={getBoothName(11)}
                            id="_x30_11"
                            x="1015.14"
                            y="498.37"
                            width="78.58"
                            height="78.58"
                            transform="translate(2206.89 807.37) rotate(166.15)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={10}
                            boothName={getBoothName(10)}
                            id="_x30_10"
                            x="978.8"
                            y="402.15"
                            width="78.58"
                            height="78.58"
                            transform="translate(2124.71 359.19) rotate(152.31)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={9}
                            boothName={getBoothName(9)}
                            id="_x30_09"
                            x="920.49"
                            y="317.41"
                            width="78.58"
                            height="78.58"
                            transform="translate(1914.72 -12.75) rotate(138.46)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={8}
                            boothName={getBoothName(8)}
                            id="_x30_08"
                            x="843.59"
                            y="249.09"
                            width="78.58"
                            height="78.58"
                            transform="translate(1621.76 -274.4) rotate(124.62)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={7}
                            boothName={getBoothName(7)}
                            id="_x30_07"
                            x="358.83"
                            y="248.39"
                            width="78.58"
                            height="78.58"
                            transform="translate(408.72 -203.39) rotate(55.38)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={6}
                            boothName={getBoothName(6)}
                            id="_x30_06"
                            x="281.74"
                            y="316.48"
                            width="78.58"
                            height="78.58"
                            transform="translate(316.66 -123.41) rotate(41.54)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={5}
                            boothName={getBoothName(5)}
                            id="_x30_05"
                            x="223.19"
                            y="401.06"
                            width="78.58"
                            height="78.58"
                            transform="translate(234.71 -71.54) rotate(27.69)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={4}
                            boothName={getBoothName(4)}
                            id="_x30_04"
                            x="186.57"
                            y="497.18"
                            width="78.58"
                            height="78.58"
                            transform="translate(134.95 -38.46) rotate(13.85)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={3}
                            boothName={getBoothName(3)}
                            id="_x30_03"
                            x="174.03"
                            y="599.28"
                            width="78.58"
                            height="78.58"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={2}
                            boothName={getBoothName(2)}
                            id="_x30_02"
                            x="186.28"
                            y="701.39"
                            width="78.58"
                            height="78.58"
                            transform="translate(-170.7 75.51) rotate(-13.85)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={1}
                            boothName={getBoothName(1)}
                            id="_x30_01"
                            x="222.62"
                            y="797.63"
                            width="78.58"
                            height="78.58"
                            transform="translate(-358.94 217.58) rotate(-27.69)"
                          />
                          <MapBooth
                            onBoothSelect={onBoothSelect}
                            highlightedBoothIndex={highlightedBoothIndex}
                            boothIndex={0}
                            boothName={getBoothName(0)}
                            id="_x30_00"
                            x="280.93"
                            y="882.37"
                            width="78.58"
                            height="78.58"
                            transform="translate(-530.64 444.13) rotate(-41.54)"
                          />
                        </g>
                      </g>
                    </svg>
                  </Col>
                </Row>
              </Container>
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
      {isSafari && currentMapSection !== BoothTabSection.ALL && (
        <div className="text-center">
          <div className="fs-1" style={{ color: '#ffd5a2' }}>
            {t(`dealers:tab.section.${currentMapSection}`)}
          </div>
          <div className="fs-4">
            {t('dealers:booths_range', {
              min: boothsIdLevel,
              max: boothsIdLevel + 53,
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Map;
